import { Grid, Typography } from "@mui/material";
import { useAppSelector } from "app/hooks";
import { DetailsTable } from "components/DetailsTable/DetailsTable";
import { ReactComponent as HelpCircleIcon } from "components/Icons/help-circle.svg";
import { Tooltip } from "components/Tooltips/Tooltip";
import { useMessage } from "hooks/intl";
import { selectDefaultLanguageData } from "reduxState/translations/translationsSlice";
import { BEWalletTypeFormat } from "types/wallets";
import { useStyles } from "./styles";
import { Interval, getExpirationPeriod, getLimitPeriod } from "./utils";

interface Props {
  walletDetails?: BEWalletTypeFormat;
}

export const WalletDetails = ({ walletDetails }: Props) => {
  const { message } = useMessage();
  const classes = useStyles();
  const defaultLang = useAppSelector(selectDefaultLanguageData);
  const walletTranslations = walletDetails
    ? walletDetails?.translations?.[defaultLang.code] || {
        name: walletDetails.code,
        description: "",
      } ||
      ""
    : {
        name: undefined,
        description: undefined,
      };

  return (
    <Grid container rowSpacing={12} mb={12}>
      <Grid item xs={12}>
        <DetailsTable
          title={message({ id: "basicSettings" })}
          items={[
            {
              label: `${message({ id: "walletName" })} (${defaultLang.name})`,
              value: walletTranslations.name,
            },
            {
              label: `${message({ id: "walletDescription" })} (${defaultLang.name})`,
              value: walletTranslations.description,
            },
            {
              label: (
                <Tooltip
                  text={
                    <>
                      <Typography className={classes.tooltipTitle}>
                        {message({ id: "walletCode" })}
                      </Typography>
                      <Typography className={classes.tooltipDesc}>
                        {message({ id: "walletCodeInfo" })}
                      </Typography>
                    </>
                  }
                >
                  <div className={classes.tootlipWrapper}>
                    <span>{message({ id: "walletCode" })}</span>
                    <HelpCircleIcon />
                  </div>
                </Tooltip>
              ),
              value: walletDetails?.code,
            },
          ]}
        />
      </Grid>
      <Grid item xs={12}>
        <DetailsTable
          title={message({ id: "unitSettings" })}
          items={[
            {
              label: message({ id: "unitSingularName" }),
              value: walletDetails?.unitSingularName,
            },
            {
              label: message({ id: "unitPluralName" }),
              value: walletDetails?.unitPluralName,
            },
            {
              label: message({ id: "unitsExpirationPeriod" }),
              value: getExpirationPeriod({ walletDetails, message }),
            },
            {
              label: message({ id: "unitsPendingPeriod" }),
              value: walletDetails
                ? walletDetails?.allTimeNotLocked
                  ? message({ id: "noPending" })
                  : message(
                      { id: "pendingForNDays" },
                      { count: walletDetails?.unitDaysLocked },
                    )
                : undefined,
            },
          ]}
        />
      </Grid>
      <Grid item xs={12}>
        <DetailsTable
          title={message({ id: "unitLimitiations" })}
          items={[
            {
              label: message({ id: "globalUnitsLimitation" }),
              value: walletDetails
                ? walletDetails?.limits?.points
                  ? getLimitPeriod({
                      interval: {
                        type:
                          walletDetails?.limits?.points.interval?.type ||
                          "inTotal",
                        value: walletDetails?.limits?.points.value,
                      } as Interval,
                      message,
                    })
                  : message({ id: "unlimited" })
                : undefined,
            },
            {
              label: message({ id: "memberUnitsLimitation" }),
              value: walletDetails
                ? walletDetails?.limits?.pointsPerMember
                  ? getLimitPeriod({
                      interval: {
                        type:
                          walletDetails?.limits?.pointsPerMember.interval
                            ?.type || "inTotal",
                        value: walletDetails?.limits?.pointsPerMember.value,
                      } as Interval,
                      message,
                    })
                  : message({ id: "unlimited" })
                : undefined,
            },
          ]}
        />
      </Grid>
    </Grid>
  );
};
